import { Link, useRouterState } from '@tanstack/react-router'
import { Menu } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useSession } from '@/lib/auth-client'
import Logo from './Logo'
import ThemeToggle from './ThemeToggle'

const activeProps = { className: 'bg-accent text-accent-foreground' }
const activeClass = 'bg-accent text-accent-foreground'

/**
 * Homepage sections shown as text tabs in the public nav (no icons up here).
 * On the homepage they scroll-spy; elsewhere they navigate to /#section.
 * Deliberately short — Use cases and Setup are content you scroll past, not
 * destinations, and seven tabs crowd the bar.
 */
const HOME_SECTIONS = [
  { id: 'features', label: 'Features' },
  { id: 'use-cases', label: 'Use cases' },
  { id: 'integrations', label: 'Integrations' },
] as const

/** Public-site nav — the marketing pages. */
const MARKETING_LINKS = [
  { to: '/pricing', label: 'Pricing' },
  { to: '/docs', label: 'Docs' },
  { to: '/blog', label: 'Blog' },
] as const

export default function Header() {
  const { data: session, isPending } = useSession()
  const authed = !isPending && !!session

  const isHome = useRouterState({
    select: (s) => s.location.pathname === '/',
  })

  // Scroll-spy for the homepage section tabs: a thin band around the
  // viewport's middle decides which section is "current".
  const [activeSection, setActiveSection] = useState<string | null>(null)
  useEffect(() => {
    if (!isHome) {
      setActiveSection(null)
      return
    }
    // Track the set of sections in the band so the highlight CLEARS when
    // none of them is current (hero, footer) instead of sticking to the
    // last one scrolled past.
    const inBand = new Set<string>()
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) inBand.add(entry.target.id)
          else inBand.delete(entry.target.id)
        }
        setActiveSection(inBand.values().next().value ?? null)
      },
      { rootMargin: '-45% 0px -50% 0px' },
    )
    for (const { id } of HOME_SECTIONS) {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    }
    return () => observer.disconnect()
  }, [isHome])

  return (
    <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-md">
      <nav className="mx-auto flex h-14 w-full max-w-5xl items-center gap-1 px-6">
        <Link
          to="/"
          aria-label="Masklabs home"
          className="mr-2 inline-flex items-center rounded-md px-2 py-1 transition-opacity hover:opacity-80"
          onClick={(e) => {
            // Already home: navigating to "/" is a no-op, so scroll to the top
            // instead of leaving the click feeling dead.
            if (isHome) {
              e.preventDefault()
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }
          }}
        >
          <Logo className="h-5 w-auto" />
        </Link>

        <div className="hidden items-center gap-1 sm:flex">
          {HOME_SECTIONS.map((s) => (
            <Button key={s.id} asChild variant="ghost" size="sm">
              {isHome ? (
                <a
                  href={`#${s.id}`}
                  aria-current={activeSection === s.id ? 'true' : undefined}
                  className={activeSection === s.id ? activeClass : undefined}
                  onClick={(e) => {
                    e.preventDefault()
                    document.getElementById(s.id)?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    })
                  }}
                >
                  {s.label}
                </a>
              ) : (
                <Link to="/" hash={s.id}>
                  {s.label}
                </Link>
              )}
            </Button>
          ))}
          {MARKETING_LINKS.map((l) => (
            <Button key={l.to} asChild variant="ghost" size="sm">
              <Link to={l.to} activeProps={activeProps}>
                {l.label}
              </Link>
            </Button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-1">
          <ThemeToggle />

          <div className="hidden items-center gap-1 sm:flex">
            {authed ? (
              <Button asChild size="sm">
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost">
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button asChild>
                  <Link to="/signup">Sign up</Link>
                </Button>
              </>
            )}
          </div>

          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="sm:hidden"
                aria-label="Open menu"
              >
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-64">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-1 px-4">
                {MARKETING_LINKS.map((l) => (
                  <MobileLink key={l.to} to={l.to} label={l.label} />
                ))}
                {authed ? (
                  <SheetClose asChild>
                    <Button asChild className="mt-1 justify-start">
                      <Link to="/dashboard">Dashboard</Link>
                    </Button>
                  </SheetClose>
                ) : (
                  <>
                    <MobileLink to="/login" label="Sign in" />
                    <SheetClose asChild>
                      <Button asChild className="mt-1 justify-start">
                        <Link to="/signup">Sign up</Link>
                      </Button>
                    </SheetClose>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  )
}

function MobileLink({ to, label }: { to: string; label: string }) {
  return (
    <SheetClose asChild>
      <Button asChild variant="ghost" className="justify-start">
        <Link to={to}>{label}</Link>
      </Button>
    </SheetClose>
  )
}
